import { ApplicationDebt, ApplicationDebtSource } from "@ploy-lib/types";
import { v4 as uuid } from "uuid";
import * as Yup from "yup";
import {
	FieldType,
	IFormTableSchema,
	ColumnMetadata,
	TableMetadata
} from "./types";

export class ApplicationDebtSchema
	implements IFormTableSchema<ApplicationDebt>
{
	tableMetadata: TableMetadata<Partial<ApplicationDebt>> = {
		dployTesterContext: "ApplicationDebt"
	};

	columnMetadata: Partial<
		Record<keyof ApplicationDebt, ColumnMetadata<ApplicationDebt>>
	> = {
		customerId: { fieldType: FieldType.Select, isRequired: true },
		amount: { fieldType: FieldType.Currency, isRequired: true },
		originalAmount: { fieldType: FieldType.Currency },
		loanType: { fieldType: FieldType.Select, isRequired: true },
		refinance: { fieldType: FieldType.Checkbox },
		comment: { fieldType: FieldType.Text },
		source: {
			fieldType: FieldType.Select,
			defaultValue: ApplicationDebtSource.Applicant
		},
		creditLimit: { fieldType: FieldType.Currency },
		lender: { fieldType: FieldType.Text },
		loanNumber: { fieldType: FieldType.Text },
		nominalInterest: { fieldType: FieldType.Percent },
		refinanceAccount: { fieldType: FieldType.Text },
		refinanceAmount: { fieldType: FieldType.Currency },
		termAmount: { fieldType: FieldType.Currency },
		maturity: { fieldType: FieldType.Number }
	};
}

export class LimitedApplicationDebtSchema
	implements IFormTableSchema<ApplicationDebt>
{
	tableMetadata: TableMetadata<Partial<ApplicationDebt>> = {
		dployTesterContext: "LimitedApplicationDebt",
		isDeletableRow: (rowData: any, cells: any) =>
			rowData["source"] === ApplicationDebtSource.Applicant
	};

	columnMetadata: Partial<
		Record<
			keyof (ApplicationDebt & { _frontendOnlyId?: string }),
			ColumnMetadata<ApplicationDebt>
		>
	> = {
		_frontendOnlyId: {
			fieldType: FieldType.Text,
			defaultValueFunc: () => uuid()
		},
		customerId: {
			fieldType: FieldType.Select,
			isRequired: true,
			isEditableFunctions: {
				isEditable: (form, row) => {
					const source = row.find(c => c.column.id === "source")?.value as
						| ApplicationDebtSource
						| undefined;

					return source === ApplicationDebtSource.Applicant;
				}
			}
		},
		amount: { fieldType: FieldType.Currency, isRequired: true },
		originalAmount: { fieldType: FieldType.Currency },
		loanType: { fieldType: FieldType.Select, isRequired: true },
		refinance: { fieldType: FieldType.Checkbox },
		willBeFulfilled: { fieldType: FieldType.Checkbox },
		comment: { fieldType: FieldType.Text },
		source: {
			fieldType: FieldType.Select,
			defaultValue: ApplicationDebtSource.Applicant
		},
		lender: { fieldType: FieldType.Text },
		loanNumber: { fieldType: FieldType.Text }
	};
}

export class LimitedRefinancedApplicationDebtSchema
	implements IFormTableSchema<ApplicationDebt>
{
	tableMetadata: TableMetadata<Partial<ApplicationDebt>> = {
		dployTesterContext: "LimitedRefinancedApplicationDebt",
		isDeletableRow: (rowData: any, cells: any) =>
			rowData["source"] === ApplicationDebtSource.Applicant
	};

	columnMetadata: Partial<
		Record<keyof ApplicationDebt, ColumnMetadata<ApplicationDebt>>
	> = {
		customerId: { fieldType: FieldType.Select, isRequired: true },
		amount: { fieldType: FieldType.Currency, isRequired: true },
		originalAmount: { fieldType: FieldType.Currency },
		loanType: { fieldType: FieldType.Select, isRequired: true },
		comment: { fieldType: FieldType.Text },
		source: { fieldType: FieldType.Select },
		lender: { fieldType: FieldType.Text, isRequired: true },
		loanNumber: { fieldType: FieldType.Text, isRequired: true },
		refinanceAccount: { fieldType: FieldType.Text, isRequired: true },
		refinanceAmount: {
			fieldType: FieldType.Currency,
			isRequired: true,
			validatorSchemas: {
				notLargerThanAmount: Yup.number()
					.min(0)
					.when(
						["amount"],
						(values: any[], schema: Yup.NumberSchema, options: any) => {
							let [amount] = values as [number];
							return schema.max(amount);
						}
					)
			}
		}
	};
}
